import React from 'react';
import { TextInput, Textarea, ChipSelector } from '@parsina/framework';

import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { UNIVERSITIES } from '../../constants/universities.js';
import {
  LEVELS, showsField, MIN_ENTRY_YEAR, MAX_ENTRY_YEAR, fieldErrorText,
} from './Onboarding.shared.js';

// The seven fields, once.
//
// Both surfaces render THIS; they differ in the frame around it (a card on a
// desktop, a full-height scroll on a phone) and in nothing else. Two copies of
// a form is how one of them ends up missing the field the other added — and the
// field most likely to be added here is the one this app must never have.
//
// ── There is no کد نظام پزشکی on this form, and there never will be ───────
//
// A student number is not a licence: it is assigned per university, collides
// across universities, and there is no registry to check it against (core.md
// §4.5). It is stored, not verified, and NOT unique — a uniqueness constraint
// would refuse a legitimate second student and verify nothing.

const DATALIST_ID = 'parsina-universities';

export default function OnboardingFields({
  values,
  onChange,
  errors = {},
  disabled = false,
  phone = '',
}) {
  const { t, locale } = useTranslation();
  const set = (key) => (e) => onChange?.({ [key]: e?.target ? e.target.value : e });
  // A key resolves through the catalogue; a `{ message }` (the BFF's own
  // sentence for an INVALID_INPUT) renders as it came.
  const err = (key) => fieldErrorText(errors[key], t);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <TextInput
        label={t('onboarding.fields.fullName')}
        required
        value={values.fullName}
        onChange={set('fullName')}
        error={err('fullName')}
        disabled={disabled}
        maxLength={120}
        autoComplete="name"
        data-testid="onboarding-full-name"
      />

      {/*
        `dir="ltr"` and a numeric input mode, because a student number is a
        digit run even when it is typed on a Persian keyboard. The value is
        FOLDED to ASCII before validation (Onboarding.shared.js), so «۹۸۱۲۳۴۵۶»
        is accepted exactly as «98123456» is.
      */}
      <TextInput
        label={t('onboarding.fields.studentNumber')}
        required
        value={values.studentNumber}
        onChange={set('studentNumber')}
        helperText={t('onboarding.fields.studentNumberHelper')}
        error={err('studentNumber')}
        disabled={disabled}
        maxLength={20}
        dir="ltr"
        inputMode="numeric"
        data-testid="onboarding-student-number"
      />

      {/*
        A datalist, not a Select: the list is long, incomplete by construction,
        and NOT validated against (core.md §4.3). A student at a university that
        is missing from src/constants/universities.js types it and it is stored
        as typed — a closed set here would lock them out of the app over a list
        this repo happens to hold.
      */}
      <TextInput
        label={t('onboarding.fields.university')}
        required
        value={values.university}
        onChange={set('university')}
        helperText={t('onboarding.fields.universityHelper')}
        error={err('university')}
        disabled={disabled}
        maxLength={120}
        list={DATALIST_ID}
        data-testid="onboarding-university"
      />
      <datalist id={DATALIST_ID} data-testid="university-options">
        {UNIVERSITIES.map((u) => (
          <option key={u.id} value={locale === 'en' ? u.en : u.fa} />
        ))}
      </datalist>

      {/* The kit's ChipSelector does not forward extra props to its wrapper, so
          the test handle lives on a div around it. */}
      <div data-testid="onboarding-level">
        <ChipSelector
          label={t('onboarding.fields.level')}
          value={values.level}
          onChange={(next) => onChange?.({ level: next })}
          options={LEVELS.map((id) => ({ value: id, label: t(`onboarding.level.${id}`) }))}
          error={err('level')}
          disabled={disabled}
        />
      </div>

      {/* «رشته» is a residency programme. A medical student does not have one,
          and asking them for it is asking them to invent an answer. */}
      {showsField(values.level) && (
        <TextInput
          label={t('onboarding.fields.field')}
          value={values.field}
          onChange={set('field')}
          helperText={t('onboarding.fields.fieldHelper')}
          error={err('field')}
          disabled={disabled}
          maxLength={80}
          data-testid="onboarding-field"
        />
      )}

      <TextInput
        label={t('onboarding.fields.entryYear')}
        value={values.entryYear}
        onChange={set('entryYear')}
        error={err('entryYear')}
        disabled={disabled}
        dir="ltr"
        inputMode="numeric"
        maxLength={4}
        placeholder={`${MIN_ENTRY_YEAR}–${MAX_ENTRY_YEAR}`}
        data-testid="onboarding-entry-year"
      />

      <Textarea
        label={t('onboarding.fields.bio')}
        value={values.bio}
        onChange={set('bio')}
        error={err('bio')}
        disabled={disabled}
        rows={3}
        maxLength={500}
        data-testid="onboarding-bio"
      />

      {/* The phone comes from the sign-in account and is changed there. It is
          rendered read-only so the student can see WHICH account they are
          completing — the one fact on this screen they did not just type. */}
      {phone ? (
        <TextInput
          label={t('onboarding.fields.phone')}
          value={phone}
          readOnly
          disabled
          dir="ltr"
          helperText={t('onboarding.fields.phoneHelper')}
          data-testid="onboarding-phone"
        />
      ) : null}
    </div>
  );
}
